import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Check, User, CheckCircle2, Circle } from 'lucide-react'
import { PlanCard, ComparisonTable } from '@/components/PropostaPublicaV2/ProposalPlanSection'
import { ProposalAcceptWizard } from '@/components/PropostaPublicaV2/ProposalAcceptWizard'
import { usePropostaPublicaV2Page } from '@/hooks/usePropostaPublicaV2Page'
import { PARCEIROS_LOGO } from '@/pages/parceiros/parceirosBrand'
import './PropostaPublicaV2.css'

export default function PropostaPublicaV2() {
  const { proposalId } = useParams<{ proposalId: string }>()
  const p = usePropostaPublicaV2Page(proposalId)

  useEffect(() => {
    document.documentElement.classList.add('proposta-publica-v2-route')
    return () => document.documentElement.classList.remove('proposta-publica-v2-route')
  }, [])

  if (p.loading) return <div className="loading-screen">Carregando proposta...</div>
  if (p.error) return <div className="error-screen"><h2>{p.error}</h2></div>
  if (!p.proposta) {
    return (
      <div className="error-screen">
        <h2>Proposta não encontrada</h2>
        <p>A proposta solicitada não foi encontrada ou os dados não puderam ser carregados.</p>
      </div>
    )
  }

  const closerEmail = p.deal?.closer?.email || ''

  return (
    <div className="public-proposal-v2-container">
      <header className="v2-header">
        <div className="v2-header-container">
          <img src={PARCEIROS_LOGO} alt="Rocket Search" className="brand-logo" />
          <div className="header-status">
            <span className={`status-pill status-pill--${p.statusInfo.cls}`}>
              <span className="status-dot-inner" /> {p.statusInfo.label}
            </span>
          </div>
        </div>
      </header>

      <main className="v2-main-content">
        <div className="layout-grid">
          <div className="left-column">
            <div className="title-block">
              <h1>Proposta Comercial</h1>
              <h2>Rocket Search</h2>
              <div className="creation-date">
                Criado em: {new Date(p.proposta.createdAt).toLocaleDateString('pt-BR')}
              </div>
            </div>

            <div className="plans-list">
              {p.itens.map((item, idx) => (
                <PlanCard
                  key={item.id || idx}
                  item={item}
                  isSelected={p.activePlans.has(idx)}
                  onToggle={() => p.togglePlan(idx)}
                />
              ))}
            </div>

            {p.itens.length >= 2 && <ComparisonTable items={p.itens} onAccept={p.handleOpenAccept} />}
          </div>

          <div className="right-column">
            <div className="v2-card consultant-card">
              <h4>Seu Consultor</h4>
              <div className="consultant-profile">
                {p.deal?.closer?.avatar && !p.failedAvatar ? (
                  <img
                    src={p.deal.closer.avatar}
                    alt="Consultor"
                    className="large-avatar"
                    onError={() => p.setFailedAvatar(true)}
                  />
                ) : (
                  <div className="large-avatar-placeholder">
                    {p.deal?.closer?.name?.substring(0, 2).toUpperCase() || <User size={22} />}
                  </div>
                )}
                <div className="consultant-details">
                  <div className="consultant-name">{p.deal?.closer?.name || 'Equipe Rocket Search'}</div>
                  <div className="consultant-role">Consultor Especializado</div>
                </div>
              </div>
              <button type="button" className="btn-outline-action" onClick={() => window.open(`mailto:${closerEmail}`)}>
                Agendar reunião
              </button>
            </div>

            <div className="v2-card investment-card">
              <h4>Investimento</h4>
              {p.selectedItems.length === 0 ? (
                <p className="no-plan-selected">Selecione um plano.</p>
              ) : (
                <div className="investment-pricing">
                  {p.selectedItems.map((item, idx) => {
                    const isRec = item.tipo === 'recorrente' || item.tipo === 'mensal'
                    const val = isRec && item.pricing?.installmentValue ? Number(item.pricing.installmentValue) : Number(item.valor || 0)
                    return (
                      <div key={idx} className="price-row recurring-row">
                        <span className="recurring-label">{item.nome || item.name}</span>
                        <span className="recurring-amount">
                          R$ {val.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          {isRec && <small> /mês</small>}
                        </span>
                      </div>
                    )
                  })}
                  <div className="price-row total-row">
                    <span className="total-label">Total</span>
                    <strong className="total-amount">
                      R$ {p.totalSelected.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      {p.hasRecurring && <small> /mês</small>}
                    </strong>
                  </div>
                </div>
              )}
              <button
                type="button"
                className="btn-accept-solid w-full mt-4"
                onClick={() => p.handleOpenAccept()}
                disabled={p.selectedItems.length === 0 || p.proposta.status === 'aceita' || p.proposta.status === 'recusada'}
              >
                {p.proposta.status === 'aceita' ? 'PROPOSTA ACEITA' : 'ACEITAR PROPOSTA'}
              </button>
              <ul className="benefits-checklist">
                {p.benefitsList.map((b, i) => (
                  <li key={i}><Check size={13} color="#10b981" /> {b}</li>
                ))}
              </ul>
            </div>

            <div className="v2-card timeline-card">
              <h4>Próximas Etapas</h4>
              <div className="timeline-container">
                {[
                  { label: 'Emissão da proposta', done: true },
                  { label: 'Aceite da proposta', done: p.proposta.status === 'aceita' },
                  { label: 'Solicitação de contrato', done: false },
                  { label: 'Contrato enviado', done: false },
                  { label: 'Contrato assinado', done: false },
                ].map((step, idx, arr) => (
                  <div key={idx} className={`timeline-item ${step.done ? 'active' : ''} ${idx === arr.length - 1 ? 'last' : ''}`}>
                    <div className="timeline-icon">
                      {step.done ? <CheckCircle2 size={16} color="#10b981" /> : <Circle size={14} color="#cbd5e1" />}
                    </div>
                    <div className="timeline-content">{step.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="v2-card terms-card">
              <h4>Termos Comerciais</h4>
              <div className="terms-list">
                <p>• <strong>Validade:</strong> {p.dados?.condicaoPagamento.vigencia || 30} dias.</p>
                <p>• <strong>Pagamento:</strong> Mensalidade emitida via boleto.</p>
                <p>• <strong>Contrato:</strong> {p.dados?.termos || 'Firmado via contrato formal após aceite.'}</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <ProposalAcceptWizard
        isOpen={p.isModalOpen}
        onClose={() => p.setIsModalOpen(false)}
        onConfirm={p.handleConfirmAccept}
        initialData={p.deal?.empresa}
      />
    </div>
  )
}
